import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import api from '../../services/api';

/**
 * @typedef {Object} Template
 * @property {number} id - Unique identifier for the template
 * @property {string} template_name - Name of the template
 * @property {TemplateItem[]} [items] - Array of template items
 */

/**
 * @typedef {Object} TemplateItem
 * @property {number} id - Unique identifier for the template item
 * @property {string} description - Description of the template item
 */

/**
 * @typedef {Object} ActionItemTemplateDialogProps
 * @property {boolean} open - Whether the dialog is open
 * @property {function} onClose - Function to call when dialog should close
 * @property {number} taskId - ID of the task to create action items for
 */

/**
 * Dialog component for selecting and applying action item templates to tasks
 * @component
 * @param {ActionItemTemplateDialogProps} props - Component props
 * @returns {React.ReactElement} The action item template dialog
 */
const ActionItemTemplateDialog = ({ open, onClose, taskId }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    /**
     * Fetches available templates from the API
     * @async
     * @returns {Promise<void>}
     */
    const fetchTemplates = async () => {
      try {
        const response = await api.get('action-item-templates/');
        setTemplates(response.data.payload);
      } catch (err) {
        setError('Failed to fetch templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  /**
   * Handles template selection and expansion logic
   * @param {Template} template - The template to select
   * @returns {void}
   */
  const handleTemplateSelect = (template) => {
    if (selectedTemplate?.id === template.id) {
      setSelectedTemplate(null);
      setExpandedItems((prev) => ({ ...prev, [template.id]: false }));
    } else {
      setSelectedTemplate(template);
      if (!template.items || template.items.length === 0) {
        setExpandedItems((prev) => ({ ...prev, [template.id]: true }));
        handleTemplateExpand(template.id, true);
      }
    }
  };

  /**
   * Fetches template items when expanding or clears them when collapsing
   * @async
   * @param {number} templateId - ID of the template to expand/collapse
   * @param {boolean} expanded - Whether the template should be expanded
   * @returns {Promise<void>}
   */
  const handleTemplateExpand = async (templateId, expanded) => {
    if (expanded) {
      try {
        const response = await api.get(`action-item-template-items/${templateId}`);
        setTemplates((prev) =>
          prev.map((template) =>
            template.id === templateId ? { ...template, items: response.data.payload } : template
          )
        );
      } catch (err) {
        console.error('Error fetching items:', err);
      }
    } else {
      setTemplates((prev) =>
        prev.map((template) =>
          template.id === templateId ? { ...template, items: [] } : template
        )
      );
    }
  };

  /**
   * Toggles the expanded state of a template
   * @param {number} templateId - ID of the template to toggle
   * @returns {void}
   */
  const toggleExpand = (templateId) => {
    const newExpanded = !expandedItems[templateId];
    setExpandedItems((prev) => ({ ...prev, [templateId]: newExpanded }));
    handleTemplateExpand(templateId, newExpanded);
  };

  /**
   * Creates action items from the selected template
   * @async
   * @returns {Promise<void>}
   */
  const handleCreate = async () => {
    if (!selectedTemplate || !selectedTemplate.items) {
      console.error('No template selected or template has no items.');
      setError('Please select a template with items.');
      return;
    }
    try {
      await Promise.all(
        selectedTemplate.items.map((item) =>
          api.post('action-item-templates/create-action-item/', {
            template_id: item.id,
            task: taskId,
          })
        )
      );
    } catch (err) {
      console.error('Error creating action items:', err);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography align="center" variant="subtitle1">
          Select Task Template
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && <div>Loading...</div>}
        {error && <Typography color="error">{error}</Typography>}
        {templates.length > 0 && (
          <List>
            {templates.map((template) => (
              <React.Fragment key={template.id}>
                <ListItem disableGutters>
                  <Checkbox
                    checked={selectedTemplate?.id === template.id}
                    onChange={() => handleTemplateSelect(template)}
                  />
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        onClick={() => toggleExpand(template.id)}
                        style={{ cursor: 'pointer', fontWeight: 'bold' }}
                      >
                        {template.template_name}
                      </Typography>
                    }
                  />
                  <IconButton edge="end" onClick={() => toggleExpand(template.id)}>
                    {expandedItems[template.id] ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </ListItem>
                {expandedItems[template.id] && (
                  <List component="div" disablePadding sx={{ pl: 10 }}>
                    {template.items &&
                      template.items.map((item) => (
                        <ListItem key={item.id} disableGutters>
                          <ListItemText
                            primary={<Typography variant="body2">{item.description}</Typography>}
                          />
                        </ListItem>
                      ))}
                  </List>
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionItemTemplateDialog;
